
import { Task, TaskComment, TaskChangeRequest } from "../../types/tasks";
import { toast } from "sonner";
import { v4 as uuidv4 } from 'uuid';

export const useTaskActions = (
  tasks: Task[], 
  setTasks: React.Dispatch<React.SetStateAction<Task[]>>,
  selectedTask: Task | null,
  setSelectedTask: React.Dispatch<React.SetStateAction<Task | null>>
) => {
  // Update task status
  const handleTaskStatusChange = (id: string, completed: boolean) => {
    setTasks(
      tasks.map((task) =>
        task.id === id
          ? { ...task, status: completed ? "completed" : "pending" }
          : task
      )
    );
    
    toast.success(completed ? "Task marked as complete" : "Task marked as pending");
  };

  // View task details
  const handleViewTask = (id: string) => {
    const task = tasks.find(task => task.id === id);
    if (task) {
      setSelectedTask(task);
      return true; // Task found and selected
    }
    return false; // Task not found
  };
  
  // Update task details
  const handleTaskUpdate = (updatedTask: Task) => {
    setTasks(tasks.map(task => task.id === updatedTask.id ? updatedTask : task));
    setSelectedTask(null);
    toast.success("Task updated successfully");
  };

  // Add a comment to a task
  const handleAddComment = (taskId: string, content: string, userId?: string) => {
    if (!userId) return;

    // Find current user's name and avatar from the tasks data
    const allAssignees = tasks.map(task => task.assignee).filter(Boolean);
    const currentUser = allAssignees.find(user => user?.id === userId);

    const newComment: TaskComment = {
      id: uuidv4(),
      authorId: userId,
      authorName: currentUser?.name || "User",
      authorAvatar: currentUser?.avatar,
      content,
      createdAt: new Date().toISOString()
    };

    // Update the tasks array
    const updatedTasks = tasks.map((task) =>
      task.id === taskId
        ? { ...task, comments: [...task.comments, newComment] }
        : task
    );
    
    setTasks(updatedTasks);
    
    // Also update the selected task to show the new comment immediately
    if (selectedTask && selectedTask.id === taskId) {
      setSelectedTask({
        ...selectedTask,
        comments: [...selectedTask.comments, newComment]
      });
    }
    
    toast.success("Comment added");
  };

  return {
    handleTaskStatusChange,
    handleViewTask,
    handleTaskUpdate,
    handleAddComment
  };
};
